// The ONE place a bidder lot page's contacts are resolved (BL-2748). A lot has
// three fixed slots, always in this order:
//
//   1. Sales rep — the lot's own rep, else the seller's rep, else the auction
//      contact (BL-1880's ladder, unchanged), shown while `showSalesRep` is on.
//   2. Seller — the seller of record, shown while `showSeller` is on.
//   3. Additional contact — four operator-typed fields, shown while
//      `showAdditionalContact` is on.
//
// There is no list and no ordering to configure. The slot defaults (rep on,
// seller off, additional off) reproduce what a lot with no contacts configured
// showed before BL-2748.
//
// An IsomorphicService (ADR 010): pure, no I/O. Deliberately NOT
// `resolveEffectiveSalesRep` — that answers "who is credited with this lot"
// (presence on the FK id, archived reps still named). This answers "who should a
// bidder call", so presence is reachability and archived reps never arrive: the
// caller filters `sales_rep.deleted_at` in its JOIN predicate (ADR 045's
// "soft-deleted rungs are the caller's problem").

import { formatSalesRepName } from '../sales-rep/format-sales-rep-name';

/** The fixed label on the Sales rep slot's person (BL-2748). */
export const SALES_REP_CONTACT_TITLE = 'Sales rep';

/** The fixed label on the Seller slot's person (BL-2748). */
export const SELLER_CONTACT_TITLE = 'Seller';

/** A person a bidder can reach — the record `AuctionContactBlock` renders. */
export interface ContactPerson {
    /**
     * The label rendered above the name: the fixed "Sales rep" / "Seller" on
     * those slots, the operator's own text on the Additional contact. Null when
     * the person carries none (the auction contact).
     */
    title?: string | null;
    /** Display name, already formatted. Null when the source has no name. */
    name: string | null;
    /** Phone as stored (E.164 or free text); the renderer formats it. */
    phone: string | null;
    email: string | null;
}

/** One active (non-archived) `sales_rep` roster row, as the ladder needs it. */
export interface SalesRepContactRung {
    firstName: string | null;
    lastName: string | null;
    phone: string | null;
    email: string | null;
}

/**
 * The lot's three contact slots as stored on `auction_lots` (BL-2748). Declared
 * here rather than imported from `@auctogon/db` so this capability package keeps
 * no dependency on the schema package (ADR 054); the columns are the stored
 * twin and `LotContactSlotFields` in `core-admin` is what validates a write.
 */
export interface LotContactSlots {
    showSalesRep: boolean;
    showSeller: boolean;
    showAdditionalContact: boolean;
    additionalContactTitle: string | null;
    additionalContactName: string | null;
    additionalContactPhone: string | null;
    additionalContactEmail: string | null;
}

/** What a lot with nothing configured shows: the rep on, the seller and the additional contact off. */
export const DEFAULT_LOT_CONTACT_SLOTS: LotContactSlots = Object.freeze({
    showSalesRep: true,
    showSeller: false,
    showAdditionalContact: false,
    additionalContactTitle: null,
    additionalContactName: null,
    additionalContactPhone: null,
    additionalContactEmail: null,
});

/**
 * Which rung supplied the Sales rep slot's person.
 *
 * `none` means no rung was reachable, which is distinct from the slot being off.
 */
export type SalesRepSlotSource = 'lot_rep' | 'seller_rep' | 'auction' | 'none';

/** True when the value carries something other than whitespace. */
function hasText(value: string | null | undefined): boolean {
    return value != null && value.trim() !== '';
}

/**
 * A person is present only when a bidder could actually reach it — a phone or an
 * email. The resolver and the lot editor's other-contact rule (BL-2783,
 * `addOtherContactReachableIssue`) both read this one test, so a contact that
 * saves is a contact the lot page shows.
 *
 * @param person - The contact to test.
 * @returns Whether either field is non-blank.
 */
export function isReachableContact(person: { phone: string | null; email: string | null }): boolean {
    return hasText(person.phone) || hasText(person.email);
}

/** Turn an active roster rep into a contact, naming it through the sole rep-name joiner. */
function repToContact(rep: SalesRepContactRung): ContactPerson {
    return {
        title: SALES_REP_CONTACT_TITLE,
        name: formatSalesRepName({ firstName: rep.firstName, lastName: rep.lastName, deletedAt: null }),
        phone: rep.phone,
        email: rep.email,
    };
}

/**
 * Resolve the Sales rep slot's person: the lot's own rep, else the seller's rep,
 * else the auction contact, else nobody. Presence-wins on reachability at every
 * rung: a rep with a name but no phone and no email is skipped, never shown
 * name-only.
 *
 * @param input - The two rep rungs (each null when unset or archived — the
 *   caller's filter) and the auction's contact fields.
 * @returns The person and the rung that supplied them; `person` is null for `none`.
 */
export function resolveSalesRepSlot(input: {
    lotOverrideRep: SalesRepContactRung | null;
    sellerRep: SalesRepContactRung | null;
    auctionContact: ContactPerson;
}): { person: ContactPerson | null; source: SalesRepSlotSource } {
    const { lotOverrideRep, sellerRep, auctionContact } = input;
    if (lotOverrideRep != null && isReachableContact(lotOverrideRep)) {
        return { person: repToContact(lotOverrideRep), source: 'lot_rep' };
    }
    if (sellerRep != null && isReachableContact(sellerRep)) {
        return { person: repToContact(sellerRep), source: 'seller_rep' };
    }
    if (isReachableContact(auctionContact)) {
        return { person: { ...auctionContact, title: null }, source: 'auction' };
    }
    return { person: null, source: 'none' };
}

/**
 * Resolve the Seller slot's person: the seller of record, labelled "Seller".
 * Null when the lot has no seller or the seller has neither phone nor email.
 *
 * @param seller - The seller's name, CRM phone and email, or null for no seller.
 * @returns The person, or null when no bidder could reach them.
 */
export function resolveSellerSlot(seller: ContactPerson | null): ContactPerson | null {
    if (seller == null || !isReachableContact(seller)) return null;
    return { title: SELLER_CONTACT_TITLE, name: seller.name, phone: seller.phone, email: seller.email };
}

/** The Additional contact slot's person, null when it carries neither phone nor email. */
function resolveAdditionalSlot(slots: LotContactSlots): ContactPerson | null {
    const person: ContactPerson = {
        title: slots.additionalContactTitle,
        name: slots.additionalContactName,
        phone: slots.additionalContactPhone,
        email: slots.additionalContactEmail,
    };
    return isReachableContact(person) ? person : null;
}

/**
 * Resolve the contacts a bidder sees on a lot page, always in the order Sales
 * rep, Seller, Additional contact. A slot that is off, or whose person has
 * neither phone nor email, contributes nothing.
 *
 * @param input - The lot's slots, the two rep rungs, the seller and the auction contact.
 * @returns The contacts to render, in order.
 */
export function resolveLotContacts(input: {
    slots: LotContactSlots;
    lotOverrideRep: SalesRepContactRung | null;
    sellerRep: SalesRepContactRung | null;
    seller: ContactPerson | null;
    auctionContact: ContactPerson;
}): ContactPerson[] {
    const { slots } = input;
    const contacts: ContactPerson[] = [];
    if (slots.showSalesRep) {
        const { person } = resolveSalesRepSlot(input);
        if (person != null) contacts.push(person);
    }
    if (slots.showSeller) {
        const seller = resolveSellerSlot(input.seller);
        if (seller != null) contacts.push(seller);
    }
    if (slots.showAdditionalContact) {
        const additional = resolveAdditionalSlot(slots);
        if (additional != null) contacts.push(additional);
    }
    return contacts;
}
